import { useCallback, useContext, useEffect, useState } from "react"

import { useLocalSearchParams } from "expo-router"

import {
  Connector,
  ConnectorAUMFee,
  ConnectorCandidate,
  ConnectorCandidateCollection,
  ConnectorCollection,
  Index,
  ProviderConnector,
} from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Heading,
  Input,
  InputField,
  Spinner,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

import { ColorField, OverrideField } from "@/components/controls"
import { AUMFeeEditor, LinkCandidateModal, LogoField } from "@/components/widgets"
import { BackendContext } from "@/context"

type Overrides = {
  name: string | null
  image_base64: string | null
  color: string | null
  url: string | null
}

// An empty string in a text field means the override was cleared out, which is
// the same as never having set one.
function normalize(value: string | null): string | null {
  if (value === null) {
    return null
  }

  const trimmed = value.trim()
  return trimmed === "" ? null : trimmed
}

export default function ConnectorDetail() {
  const backend = useContext(BackendContext)
  const { connector_id } = useLocalSearchParams<{ connector_id: string }>()

  const [connector, setConnector] = useState<Connector>()
  const [candidates, setCandidates] = useState<ConnectorCandidate[]>([])
  const [preferred, setPreferred] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<Overrides | null>(null)
  const [aumFee, setAUMFee] = useState<ConnectorAUMFee | null>(null)
  const [linking, setLinking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    const collection = data.link("connectors")?.resolve() as ConnectorCollection | undefined
    if (!collection) {
      setError("Connectors resource is not available")
      return
    }

    const model = collection.model(connector_id)
    await model.fetch({ reload: true })

    const candidateCollection = model.link("candidates")?.resolve() as ConnectorCandidateCollection | undefined
    if (candidateCollection) {
      await candidateCollection.fetch({ reload: true })
      setCandidates(candidateCollection.models)

      const current = candidateCollection.models.find((candidate) => candidate.attributes.preferred)
      setPreferred(current ? providerConnectorID(current) : null)
    }

    const aumFeeModel = model.link("aum_fee")?.resolve() as ConnectorAUMFee | undefined
    if (aumFeeModel) {
      await aumFeeModel.fetch({ reload: true })
      setAUMFee(aumFeeModel)
    }

    setConnector(model)
    // Only curation tooling is served the overrides, so an admin token is what
    // makes this page work at all.
    setOverrides({
      name: null,
      image_base64: null,
      color: null,
      url: null,
      ...model.attributes.overrides,
    })
  }, [backend, connector_id])

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)))
  }, [load])

  function providerConnectorID(candidate: ConnectorCandidate): string {
    const embedded = candidate.embed("provider_connector") as ProviderConnector | null
    return embedded?.id ?? ""
  }

  function inherited(field: keyof Overrides): string | null {
    const provider = connector?.embed("provider_connector") as ProviderConnector | null
    if (!provider) {
      return null
    }

    switch (field) {
      case "name":
        return provider.attributes.name ?? null
      case "image_base64":
        return provider.attributes.image_base64 ?? null
      case "color":
        return provider.attributes.color ?? null
      case "url":
        return provider.attributes.url ?? null
    }
  }

  function update(field: keyof Overrides, value: string | null) {
    setOverrides((current) => (current ? { ...current, [field]: value } : current))
  }

  async function send(link: string, init: RequestInit): Promise<Response> {
    const target = connector?.link(link)
    if (!target) {
      throw new Error(`The ${link} link is not available`)
    }

    const response = await target.send(init)
    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || response.statusText)
    }

    return response
  }

  async function save() {
    if (!connector || !overrides || !preferred) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      // A PUT with every override: one sent as null is cleared, and the value
      // falls back to the provider again.
      await send("self", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          preferred_provider_connector: preferred,
          name: normalize(overrides.name),
          image_base64: overrides.image_base64,
          color: normalize(overrides.color),
          url: normalize(overrides.url),
        }),
      })

      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function linkCandidate(providerConnectorID: string) {
    await send("candidates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider_connector: providerConnectorID }),
    })

    await load()
  }

  async function unlinkCandidate(candidate: ConnectorCandidate) {
    setError(null)

    try {
      const self = candidate.link("self")
      if (!self) {
        throw new Error("The candidate link is not available")
      }

      const response = await self.send({ method: "DELETE" })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || response.statusText)
      }

      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function setPrecedence(candidate: ConnectorCandidate, precedence: number) {
    setError(null)

    try {
      const self = candidate.link("self")
      if (!self) {
        throw new Error("The candidate link is not available")
      }

      const response = await self.send({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ precedence }),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || response.statusText)
      }

      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (!connector || !overrides) {
    return (
      <Box className="p-4">
        {error ? <Text className="text-sm text-foreground-secondary">{error}</Text> : <Spinner size="small" />}
      </Box>
    )
  }

  return (
    <Box className="flex-1 min-h-0 overflow-y-auto p-4">
      <VStack className="gap-20 max-w-3xl shrink-0">
        <Heading size="xl">
          {connector.attributes.name}{" "}
          <Text className="text-sm text-foreground-secondary">
            {connector.id} · {connector.attributes.active ? "active" : "inactive - no candidate can serve it"}
          </Text>
        </Heading>

        <VStack className="gap-12">
          <VStack>
            <Heading size="md">Overrides</Heading>
            <Text className="text-xs text-foreground-secondary">
              Every field falls back to the provider connector serving this connector until you override it.
            </Text>
          </VStack>

          <OverrideField
            label="Name"
            value={overrides.name}
            inherited={inherited("name")}
            onChange={(value) => update("name", value)}
          />
          <LogoField
            value={overrides.image_base64}
            inherited={inherited("image_base64")}
            onChange={(value) => update("image_base64", value)}
          />
          <ColorField
            value={overrides.color}
            inherited={inherited("color")}
            onChange={(value) => update("color", value)}
          />
          <OverrideField
            label="URL"
            value={overrides.url}
            inherited={inherited("url")}
            onChange={(value) => update("url", value)}
          />
        </VStack>

        <VStack className="gap-4">
          <Heading size="sm">
            Candidates{" "}
            <Text className="text-xs text-foreground-secondary">
              New connections go to the preferred candidate. Precedence only used for failover - the lowest value is
              tried first.
            </Text>
          </Heading>

          <VStack className="gap-2">
            {candidates.map((candidate) => {
              const provider = candidate.embed("provider_connector") as ProviderConnector | null
              const id = providerConnectorID(candidate)
              const isPreferred = preferred === id

              return (
                <HStack
                  key={candidate.id}
                  className="items-center justify-between gap-4 rounded-sm border border-border-medium px-6 py-2"
                >
                  <VStack className="flex-1">
                    <Text className="text-sm text-foreground-primary">
                      {provider?.attributes.name ?? "Unknown"}
                      {provider && !provider.attributes.active ? " · inactive" : ""}
                    </Text>
                    <Text className="text-xs text-foreground-secondary">
                      {provider?.attributes.provider} · {provider?.attributes.url ?? "no url"}
                    </Text>
                  </VStack>

                  <HStack className="items-center gap-2">
                    <Text className="text-xs text-foreground-secondary">Precedence</Text>
                    <Input
                      variant="outline"
                      size="sm"
                      className="w-20"
                    >
                      <InputField
                        defaultValue={String(candidate.attributes.precedence)}
                        keyboardType="numeric"
                        onSubmitEditing={(event: { nativeEvent: { text: string } }) => {
                          const parsed = parseInt(event.nativeEvent.text, 10)
                          if (!Number.isNaN(parsed)) {
                            void setPrecedence(candidate, parsed)
                          }
                        }}
                      />
                    </Input>

                    <Button
                      size="sm"
                      variant={isPreferred ? "solid" : "outline"}
                      isDisabled={isPreferred}
                      onPress={() => setPreferred(id)}
                    >
                      <ButtonText className={isPreferred ? "text-foreground-primary" : undefined}>
                        {isPreferred ? "Preferred" : "Prefer"}
                      </ButtonText>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={isPreferred}
                      onPress={() => void unlinkCandidate(candidate)}
                    >
                      <ButtonText>Unlink</ButtonText>
                    </Button>
                  </HStack>
                </HStack>
              )
            })}
          </VStack>

          <Button
            size="sm"
            variant="outline"
            onPress={() => setLinking(true)}
          >
            <ButtonText>Link provider connector</ButtonText>
          </Button>
        </VStack>

        {error ? (
          <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
            <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
          </Box>
        ) : null}

        <HStack className="gap-2">
          <Button
            action="primary"
            className="bg-background-contrast"
            isDisabled={saving || !preferred}
            onPress={() => void save()}
          >
            <ButtonText className="text-foreground-contrast">{saving ? "Saving…" : "Save"}</ButtonText>
          </Button>
        </HStack>

        {aumFee ? <AUMFeeEditor aumFee={aumFee} /> : null}
      </VStack>

      <LinkCandidateModal
        isOpen={linking}
        onClose={() => setLinking(false)}
        onLink={linkCandidate}
      />
    </Box>
  )
}
